import { addMonths, periodContaining } from '@fd/finance-core';
import { newId } from '@fd/schemas';
import { useEffect, useRef } from 'react';
import { AUTO_SETTLED, findAutoSettlement } from '../app/auto-settle.ts';
import { today, useSettlementContext } from '../app/data.ts';
import { useT } from '../app/i18n.tsx';
import { unpairedSettlementSides } from '../app/settlement-counterpart.ts';
import { settlementIn } from '../app/settlement-scope.ts';
import { useSession } from '../app/session.tsx';

/**
 * Records a finished month's settlement by itself once its bank transfer has come in (auto-settle.ts):
 * the payment becomes a settlement payment, the settlement carries a note, and the bell says so. Only
 * with everything sent and received, so two devices do not both record it from stale data. Also marks the other
 * side of every settlement payment when both accounts are in the app (settlement-counterpart.ts).
 */
export function AutoSettle() {
  const t = useT();
  const { engine, householdId, status, requestSync } = useSession();
  const data = useSettlementContext();
  const tried = useRef(new Set<string>());

  useEffect(() => {
    if (!data || !householdId || !status.online || status.syncing || status.queued > 0 || status.failed > 0 || !status.lastSuccessAt) return;
    const ownersOf = (accountId: string) => data.context.accounts.get(accountId)?.ownerPersonIds ?? [];

    // Both sides of one settlement payment (settlement-counterpart.ts): the side still counted as money in or out
    // becomes a settlement payment too, each naming the other's account. Settlements recorded before 01.10.2026
    // are mended the same way. The month's auto-settlement waits for the next pass, after these are sent.
    const pairs = unpairedSettlementSides(
      data.transactions.map((tx) => ({
        id: tx.id,
        accountId: tx.accountId,
        bookingDate: tx.bookingDate,
        amountMinor: BigInt(tx.amountMinor),
        currency: tx.currency,
        kind: tx.kind,
        counterpartAccountId: tx.counterpartAccountId,
        deleted: tx.deletedAt !== null,
      })),
      ownersOf,
    ).filter(({ counterpart }) => !tried.current.has(`pair:${counterpart.id}`));
    if (pairs.length > 0) {
      for (const { counterpart } of pairs) tried.current.add(`pair:${counterpart.id}`);
      void (async () => {
        for (const { payment, counterpart } of pairs) {
          await engine.update(householdId, 'transaction', counterpart.id, {
            kind: 'SETTLEMENT_PAYMENT',
            expenseType: null,
            categoryId: null,
            counterpartAccountId: payment.accountId,
          });
          if (payment.counterpartAccountId === null) {
            await engine.update(householdId, 'transaction', payment.id, { counterpartAccountId: counterpart.accountId });
          }
        }
        requestSync();
      })();
      return;
    }

    const now = today();
    const thisMonth = `${now.slice(0, 8)}01`;
    const months = [-1, -2].map((back) => {
      const period = periodContaining('MONTH', addMonths(thisMonth, back));
      const result = settlementIn(data, 'period', period);
      return { start: period.start, end: period.end, ledgers: result.ledgers, items: result.items };
    });
    const live = data.settlements.filter((s) => s.deletedAt === null && s.status === 'SETTLED');
    const found = findAutoSettlement({
      today: now,
      months,
      entries: data.transactions.map((tx) => ({
        id: tx.id,
        accountId: tx.accountId,
        bookingDate: tx.bookingDate,
        amountMinor: BigInt(tx.amountMinor),
        currency: tx.currency,
        kind: tx.kind,
        status: tx.status,
        source: tx.source,
        paymentType: tx.paymentType,
        deleted: tx.deletedAt !== null,
      })),
      ownersOf,
      usedPayments: new Set(live.flatMap((s) => s.transfers.flatMap((tr) => (tr.paymentTransactionId ? [tr.paymentTransactionId] : [])))),
    });
    if (!found) return;
    const key = `${found.start}:${found.currency}:${found.transfers.map((tr) => tr.paymentTransactionId).join(',')}`;
    if (tried.current.has(key)) return;
    tried.current.add(key);
    void (async () => {
      // As on Settlements: the payment is neither spending nor income, the settlement carries it.
      for (const transfer of found.transfers) {
        await engine.update(householdId, 'transaction', transfer.paymentTransactionId, { kind: 'SETTLEMENT_PAYMENT', expenseType: null, categoryId: null });
      }
      await engine.create(householdId, 'settlement', newId(), {
        periodStart: found.start,
        periodEnd: found.end,
        settledOn: now,
        currency: found.currency,
        status: 'SETTLED',
        note: t.t('settlement.autoNote', { month: t.date(found.start, 'monthYear') }),
        ruleSnapshot: AUTO_SETTLED,
        transfers: found.transfers.map((transfer) => ({
          id: newId(),
          fromPersonId: transfer.fromPersonId,
          toPersonId: transfer.toPersonId,
          amountMinor: String(transfer.amountMinor),
          paymentTransactionId: transfer.paymentTransactionId,
        })),
        items: found.items.slice(0, 20_000),
      });
      requestSync();
    })();
  }, [data, householdId, status.online, status.syncing, status.queued, status.failed, status.lastSuccessAt, engine, requestSync, t]);

  return null;
}
